import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, CreditCard, Landmark, Copy } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import api, { formatApiError, downloadFile } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function Field({ label, id, testid, hint, wrapperClassName, ...props }) {
  return (
    <div className={wrapperClassName}>
      <label className="label-elegant" htmlFor={id}>{label}</label>
      <input id={id} data-testid={testid} className="input-elegant" {...props} />
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function RichiestaConferma({ conferma, iban, paypalLink }) {
  const [copiato, setCopiato] = useState(false);
  const pagamento = conferma.pagamento || {};
  const metodoLabel = pagamento.metodo === "paypal" ? "PayPal" : "Bonifico bancario";

  const copiaIban = async () => {
    try {
      await navigator.clipboard.writeText(iban);
      setCopiato(true);
      setTimeout(() => setCopiato(false), 2500);
    } catch {
      setCopiato(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl px-4 py-16 sm:px-6" data-testid="richiesta-conferma">
      <div className="card-elegant border-bordeaux/30 bg-bordeaux-ultralight text-center">
        <CheckCircle2 className="mx-auto h-14 w-14 text-bordeaux" />
        <h1 className="mt-4 font-heading text-2xl font-bold text-bordeaux-dark sm:text-3xl">Richiesta inviata con successo</h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground/80">
          Abbiamo ricevuto la tua richiesta di certificato di agibilità. Ti abbiamo inviato una email di conferma.
        </p>
        <div className="mx-auto mt-6 max-w-sm rounded-lg bg-white p-5 text-left text-sm shadow-sm">
          <p><span className="text-muted-foreground">Numero pratica:</span> <strong className="text-bordeaux" data-testid="conferma-numero-pratica">{conferma.numero_pratica}</strong></p>
          <p className="mt-2"><span className="text-muted-foreground">Data esibizione:</span> <strong>{conferma.data_esibizione}</strong></p>
          <p className="mt-2"><span className="text-muted-foreground">Locale:</span> <strong>{conferma.nome_locale}</strong></p>
          <p className="mt-2"><span className="text-muted-foreground">Stato richiesta:</span> <strong>{conferma.stato}</strong></p>
          <p className="mt-2"><span className="text-muted-foreground">Metodo di pagamento:</span> <strong data-testid="conferma-metodo-pagamento">{metodoLabel}</strong></p>
          <p className="mt-2"><span className="text-muted-foreground">Importo servizio:</span> <strong>€ {Number(pagamento.importo || 0).toFixed(2)}</strong></p>
          <p className="mt-2"><span className="text-muted-foreground">Stato pagamento:</span> <strong data-testid="conferma-stato-pagamento">{pagamento.stato || "-"}</strong></p>
          {pagamento.transazione_id && (
            <p className="mt-2"><span className="text-muted-foreground">ID transazione PayPal:</span> <strong data-testid="conferma-transazione-id">{pagamento.transazione_id}</strong></p>
          )}
        </div>

        {pagamento.metodo === "bonifico" && (
          <div className="mx-auto mt-4 max-w-sm rounded-lg border border-bordeaux/30 bg-white p-5 text-left text-sm shadow-sm" data-testid="conferma-bonifico-box">
            <p className="font-heading font-bold text-bordeaux-dark">Coordinate bancarie ENPALSBOX</p>
            <p className="mt-2 text-muted-foreground">IBAN:</p>
            <p className="font-mono text-base font-bold tracking-wider text-foreground" data-testid="conferma-iban">{iban}</p>
            <button onClick={copiaIban} data-testid="copia-iban-button" className="btn-outline-bordeaux mt-3 !px-4 !py-2 text-xs">
              <Copy className="h-3.5 w-3.5" /> {copiato ? "IBAN COPIATO" : "COPIA IBAN"}
            </button>
            <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
              Effettua il bonifico indicando nella causale il numero della pratica.
            </p>
            <p className="mt-1 text-xs">
              La tua pratica: <strong className="text-bordeaux">{conferma.numero_pratica}</strong>
            </p>
            <p className="mt-3 inline-block rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="conferma-pagamento-attesa">
              PAGAMENTO IN ATTESA DI VERIFICA
            </p>
          </div>
        )}

        {pagamento.metodo === "paypal" && !pagamento.transazione_id && paypalLink && (
          <div className="mx-auto mt-4 max-w-sm rounded-lg border border-bordeaux/30 bg-white p-5 text-left text-sm shadow-sm" data-testid="conferma-paypal-box">
            <p className="font-heading font-bold text-bordeaux-dark">Completa il pagamento con PayPal</p>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              Versa € {Number(pagamento.importo || 0).toFixed(2)} a enpalsbox@enpalsbox.it tramite PayPal,
              indicando nella nota il numero della pratica: <strong className="text-bordeaux">{conferma.numero_pratica}</strong>
            </p>
            <a href={`${paypalLink}&amount=${Number(pagamento.importo || 0).toFixed(2)}`} target="_blank" rel="noopener noreferrer" data-testid="paypal-pay-link" className="btn-bordeaux mt-3 w-full !py-3">
              PAGA ORA CON PAYPAL
            </a>
            <p className="mt-3 inline-block rounded-full border border-amber-300 bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-800" data-testid="conferma-pagamento-attesa">
              PAGAMENTO IN ATTESA DI VERIFICA
            </p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            onClick={() => downloadFile(`/agibilita/${conferma.id}/ricevuta`, `ricevuta-${conferma.numero_pratica}.pdf`)}
            data-testid="conferma-ricevuta-btn"
            className="btn-outline-bordeaux"
          >
            SCARICA RICEVUTA PDF
          </button>
          <Link to="/le-mie-richieste" data-testid="conferma-my-requests-btn" className="btn-bordeaux">LE MIE RICHIESTE</Link>
          <Link to="/area-personale" data-testid="conferma-dashboard-btn" className="btn-outline-bordeaux">AREA PERSONALE</Link>
        </div>
      </div>
    </div>
  );
}

function validateAgibilitaForm(form) {
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_certificato.trim())) {
    return "Inserisci un indirizzo email valido a cui inviare il certificato";
  }
  if (!/^\d{5}$/.test(form.cap.trim())) {
    return "C.A.P. non valido: devono essere 5 cifre";
  }
  if (!/^[A-Za-z]{2}$/.test(form.provincia.trim())) {
    return "Provincia non valida: inserisci la sigla di 2 lettere (es. RM)";
  }
  if (!form.privacy) {
    return "Devi accettare le regole sulla privacy per inviare la richiesta";
  }
  if (isNaN(parseFloat(form.importo)) || parseFloat(form.importo) <= 0) {
    return "Inserisci un importo valido in euro";
  }
  if (isNaN(parseFloat(form.importo_servizio)) || parseFloat(form.importo_servizio) <= 0) {
    return "Inserisci l'importo del servizio in euro (maggiore di zero)";
  }
  return null;
}

export default function RichiestaAgibilitaPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    nome_cognome: "", nome_locale: "", indirizzo_locale: "", citta: "", provincia: "", cap: "",
    attivita: "", data_esibizione: "", importo: "", email_certificato: "", privacy: false, importo_servizio: "51.00",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [conferma, setConferma] = useState(null);
  const [metodo, setMetodo] = useState(null);
  const [payConfig, setPayConfig] = useState(null);

  useEffect(() => {
    api.get("/pagamenti/config").then((res) => setPayConfig(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, nome_cognome: `${user.nome} ${user.cognome}`, email_certificato: user.email }));
    }
  }, [user]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e, metodoOverride = null, transazioneId = null) => {
    e?.preventDefault();
    setError("");
    const metodoScelto = metodoOverride || metodo;
    const errore = validateAgibilitaForm(form);
    if (errore) {
      setError(errore);
      return;
    }
    if (!metodoScelto) {
      setError("Seleziona un metodo di pagamento per continuare");
      return;
    }
    setLoading(true);
    try {
      const res = await api.post("/agibilita", {
        ...form,
        importo: parseFloat(form.importo),
        importo_servizio: parseFloat(form.importo_servizio),
        metodo_pagamento: metodoScelto,
        paypal_transazione_id: transazioneId,
      });
      setConferma(res.data);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (conferma) {
    return <RichiestaConferma conferma={conferma} iban={payConfig?.iban || ""} paypalLink={payConfig?.paypal_link || ""} />;
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-12 sm:px-6" data-testid="richiesta-agibilita-page">
      <h1 className="section-title">Richiedi Certificato di Agibilità</h1>
      <p className="mt-3 text-muted-foreground">
        Compila il modulo con i dati della tua esibizione. I campi contrassegnati con * sono obbligatori.
      </p>

      {error && (
        <div data-testid="richiesta-error" className="mt-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <form onSubmit={submit} noValidate className="card-elegant mt-8 space-y-5" data-testid="agibilita-request-form">
        <Field label="1. Nome e Cognome *" id="ag-nome" testid="agibilita-nome-cognome-input" required
          value={form.nome_cognome} onChange={set("nome_cognome")}
          hint="Precompilato con i dati del tuo profilo: verifica che siano corretti." />
        <Field label="2. Nome del locale dove ti esibisci *" id="ag-locale" testid="agibilita-nome-locale-input" required
          value={form.nome_locale} onChange={set("nome_locale")} />
        <Field label="3. Indirizzo del locale *" id="ag-indirizzo" testid="agibilita-indirizzo-input" required
          value={form.indirizzo_locale} onChange={set("indirizzo_locale")} />
        <div className="grid gap-4 sm:grid-cols-3">
          <Field wrapperClassName="sm:col-span-1" label="4. Città *" id="ag-citta" testid="agibilita-citta-input" required
            value={form.citta} onChange={set("citta")} />
          <Field label="5. Provincia *" id="ag-provincia" testid="agibilita-provincia-input" className="input-elegant uppercase"
            required maxLength={2} placeholder="RM" value={form.provincia} onChange={set("provincia")} />
          <Field label="6. C.A.P. *" id="ag-cap" testid="agibilita-cap-input" required maxLength={5} inputMode="numeric"
            placeholder="00100" value={form.cap} onChange={set("cap")} />
        </div>
        <Field label="7. Attività *" id="ag-attivita" testid="agibilita-attivita-input" required
          placeholder="Es. cantante, D.J., musicista" value={form.attivita} onChange={set("attivita")} />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="8. Data dell'esibizione *" id="ag-data" testid="agibilita-data-input" type="date" required
            value={form.data_esibizione} onChange={set("data_esibizione")} />
          <Field label="9. Importo pattuito (€) *" id="ag-importo" testid="agibilita-importo-input" type="number"
            min="0.01" step="0.01" required placeholder="Es. 150.00" value={form.importo} onChange={set("importo")} />
        </div>
        <Field label="10. Email a cui inviare il certificato *" id="ag-email" testid="agibilita-email-input" type="email"
          required value={form.email_certificato} onChange={set("email_certificato")} />
        <label className="flex items-start gap-3 text-sm text-foreground/80" data-testid="agibilita-privacy-label">
          <input type="checkbox" data-testid="agibilita-privacy-checkbox" className="mt-0.5 h-4 w-4 accent-[#7A1C30]" checked={form.privacy} onChange={set("privacy")} />
          11. Accetto le regole sulla privacy *
        </label>

        <div className="rounded-xl border border-bordeaux/30 bg-bordeaux-ultralight p-5" data-testid="pagamento-section">
          <p className="font-heading text-lg font-semibold text-bordeaux-dark">PAGAMENTO</p>
          <p className="mt-1 text-sm text-muted-foreground">Servizio Certificato di Agibilità</p>
          <div className="mt-2 max-w-xs">
            <label className="label-elegant" htmlFor="pag-importo">Importo del servizio (€) *</label>
            <input
              id="pag-importo"
              type="number"
              min="0.01"
              step="0.01"
              data-testid="pagamento-importo-input"
              className="input-elegant"
              value={form.importo_servizio}
              onChange={set("importo_servizio")}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Importo concordato con ENPALSBOX per il servizio (valore indicativo: € 51,00).
            </p>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => setMetodo("paypal")}
              data-testid="paga-paypal-button"
              className={`flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-bold transition-colors ${
                metodo === "paypal" ? "border-bordeaux bg-bordeaux text-white" : "border-bordeaux/40 bg-white text-bordeaux hover:border-bordeaux"
              }`}
            >
              <CreditCard className="h-4 w-4" /> PAGA CON PAYPAL
            </button>
            <button
              type="button"
              onClick={() => setMetodo("bonifico")}
              data-testid="paga-bonifico-button"
              className={`flex items-center justify-center gap-2 rounded-lg border-2 px-4 py-3 text-sm font-bold transition-colors ${
                metodo === "bonifico" ? "border-bordeaux bg-bordeaux text-white" : "border-bordeaux/40 bg-white text-bordeaux hover:border-bordeaux"
              }`}
            >
              <Landmark className="h-4 w-4" /> PAGA CON BONIFICO BANCARIO
            </button>
          </div>

          {metodo === "paypal" && payConfig?.paypal_enabled && (
            <div className="mt-4" data-testid="paypal-buttons-container">
              <PayPalScriptProvider options={{ clientId: payConfig.paypal_client_id, currency: "EUR" }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "gold", label: "pay" }}
                  forceReRender={[form.importo_servizio]}
                  createOrder={async () => {
                    const errore = validateAgibilitaForm(form);
                    if (errore) {
                      setError(errore);
                      throw new Error(errore);
                    }
                    const res = await api.post("/pagamenti/paypal/ordine", { importo: parseFloat(form.importo_servizio) });
                    return res.data.order_id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const res = await api.post("/pagamenti/paypal/cattura", { order_id: data.orderID });
                      await submit(null, "paypal", res.data.transazione_id);
                    } catch (err) {
                      setError(formatApiError(err, "Pagamento PayPal non riuscito. Riprova o scegli il bonifico bancario."));
                    }
                  }}
                  onError={() => setError("Errore PayPal. Riprova o scegli il bonifico bancario.")}
                />
              </PayPalScriptProvider>
              <p className="mt-2 text-xs text-muted-foreground">
                Pagamento sicuro tramite PayPal verso enpalsbox@enpalsbox.it. Nessun dato della carta viene memorizzato.
              </p>
            </div>
          )}
          {metodo === "paypal" && payConfig && !payConfig.paypal_enabled && (
            <div className="mt-4 rounded-lg bg-white p-4 text-sm" data-testid="paypal-non-configurato">
              <p className="font-semibold text-bordeaux-dark">Pagamento PayPal verso enpalsbox@enpalsbox.it</p>
              <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                Clicca INVIA RICHIESTA: nella schermata di conferma troverai il pulsante PAGA ORA CON PAYPAL per versare l'importo del servizio tramite PayPal, indicando il numero della pratica nella nota del pagamento.
              </p>
            </div>
          )}

          {metodo === "bonifico" && (
            <div className="mt-4 rounded-lg bg-white p-4 text-sm" data-testid="bonifico-info-box">
              <p className="font-semibold text-bordeaux-dark">Coordinate bancarie ENPALSBOX</p>
              <p className="mt-1 text-muted-foreground">
                IBAN: <span className="font-mono font-bold tracking-wider text-foreground" data-testid="bonifico-iban">{payConfig?.iban}</span>
              </p>
              <p className="mt-2 text-xs text-muted-foreground">
                Clicca INVIA RICHIESTA: riceverai il numero di pratica da indicare nella causale del bonifico.
              </p>
            </div>
          )}
        </div>

        {!(metodo === "paypal" && payConfig?.paypal_enabled) && (
          <button type="submit" disabled={loading} data-testid="agibilita-submit-button" className="btn-bordeaux w-full !py-4">
            {loading ? "INVIO IN CORSO..." : "INVIA RICHIESTA"}
          </button>
        )}
      </form>
    </div>
  );
}
